import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { App as CapApp } from '@capacitor/app';
import { Browser } from '@capacitor/browser';

// Checkout in-app: ConfirmOrderRequest (shop), UserRequestCard (activity_request), CompanyDetails (Stripe Connect onboarding).
export const PENDING_CHECKOUT_KEY = 'ticino-wild-pending-checkout';

// Path che fanno parte del flusso checkout Stripe. Solo per questi facciamo cleanup PENDING_CHECKOUT_KEY + Browser.close.
const CHECKOUT_RELATED_PREFIXES = [
  '/stripe-return',
  '/product-purchase-success',
  '/request/success',
  '/company-details'
];

export const isCheckoutRelatedPath = (path) => {
  if (!path) return false;
  const pathname = path.split('?')[0];
  return CHECKOUT_RELATED_PREFIXES.some(prefix => pathname.startsWith(prefix));
};

/** Percorso + query per React Router (es. /request/success?session_id=…&requestId=…). */
export function internalPathFromDeepLink(rawUrl) {
  if (!rawUrl || typeof rawUrl !== 'string') return '';
  try {
    const u = new URL(rawUrl);
    const p = `${u.pathname || ''}${u.search || ''}`;
    if (p && p !== '/') {
      return p.startsWith('/') ? p : `/${p}`;
    }
  } catch {
    // ticinowild:///… a volte non passa new URL() su alcune versioni / formati
  }
  const lower = rawUrl.toLowerCase();
  const marker = 'ticinowild:';
  const i = lower.indexOf(marker);
  if (i >= 0) {
    const rest = rawUrl.slice(i + marker.length).replace(/^\/+/, '');
    if (rest) return rest.startsWith('/') ? rest : `/${rest}`;
  }
  return '';
}

const DeepLinkHandler = () => {
  const navigate = useNavigate();

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    let urlHandle, browserHandle;

    // ticinowild:/// (Android post-Stripe) o Universal Link: naviga e tenta Browser.close.
    // Su iOS il checkout non usa più redirect allo schema custom; questo resta per https applinks.
    // Dopo Stripe su iOS vale browserFinished + pending (sotto).
    CapApp.addListener('appUrlOpen', async (event) => {
      const path = internalPathFromDeepLink(event.url);
      const isCheckoutFlow = isCheckoutRelatedPath(path);

      // Cleanup pending + close browser solo per path del flusso checkout.
      // Per altri deep link (es. /account/delete-confirm) non interferiamo con uno checkout in corso.
      if (isCheckoutFlow) {
        localStorage.removeItem(PENDING_CHECKOUT_KEY);
      }

      if (path && path !== '/') {
        navigate(path, { replace: true });
      }

      if (isCheckoutFlow) {
        setTimeout(async () => {
          try { await Browser.close(); } catch {}
        }, 300);
      }
    }).then(h => { urlHandle = h; });

    // Chiusura del browser in-app (primario su iOS dopo Stripe): pending → schermata di successo.
    Browser.addListener('browserFinished', () => {
      const raw = localStorage.getItem(PENDING_CHECKOUT_KEY);
      if (!raw) return;

      localStorage.removeItem(PENDING_CHECKOUT_KEY);

      try {
        const pending = JSON.parse(raw);

        if (pending.type === 'shop_order' && pending.orderId) {
          navigate(
            `/product-purchase-success?orderId=${encodeURIComponent(pending.orderId)}`,
            { replace: true }
          );
        } else if (pending.type === 'activity_request' && pending.requestId && pending.sessionId) {
          navigate(
            `/request/success?session_id=${encodeURIComponent(pending.sessionId)}&requestId=${encodeURIComponent(pending.requestId)}`,
            { replace: true }
          );
        } else if (
          pending.type === 'connect_onboarding' &&
          pending.companyId &&
          pending.stripeAccountId
        ) {
          navigate(
            `/company-details?companyId=${encodeURIComponent(pending.companyId)}&stripeAccountId=${encodeURIComponent(pending.stripeAccountId)}&stripeStatus=verified`,
            { replace: true }
          );
        }
      } catch {}
    }).then(h => { browserHandle = h; });

    return () => {
      urlHandle?.remove();
      browserHandle?.remove();
    };
  }, [navigate]);

  return null;
};

export default DeepLinkHandler;
